/**
 * Kaching Cart Enhancements
 * —————————————————————————
 * Injects product type ("Inspired by …") and Judge.me rating badges
 * into Kaching cart items (single products & bundle components).
 *
 * Key feature: caches product data so quantity changes don't cause
 * elements to "blink" — re-injection from cache is instant (no API calls).
 */

(function () {
  'use strict';

  const PRODUCT_TYPE_CLASS = 'product-type-text';
  const RATING_CLASS = 'cart-item-rating';
  const PROCESSED_ATTR = 'data-kce-processed';

  let debounceTimer = null;
  let isUpdating = false;

  /* ———————————————— Data Cache ———————————————— */
  // handle → { type: string, handle: string }
  const productCache = new Map();
  // Cart items cache (refreshed only when item count changes)
  let cachedCartItems = null;
  let cachedCartItemCount = null;

  /* ———————————————— CSS Injection ———————————————— */
  (function injectCSS() {
    const style = document.createElement('style');
    style.textContent = `
      .kaching-cart-item__info {
        display: flex !important;
        flex-direction: column !important;
      }
      .kaching-cart-item__info > .kaching-cart-item__title {
        order: -3 !important;
      }
      .kaching-cart-item__info > .${PRODUCT_TYPE_CLASS} {
        order: -2 !important;
      }
      .kaching-cart-item__info > .${RATING_CLASS} {
        order: -1 !important;
      }
      .kaching-cart-item-component__content {
        display: flex !important;
        flex-direction: column !important;
      }
      .kaching-cart-item-component__content > .kaching-cart-item-component__content-top {
        order: -3 !important;
      }
      .kaching-cart-item-component__content > .${PRODUCT_TYPE_CLASS} {
        order: -2 !important;
      }
      .kaching-cart-item-component__content > .${RATING_CLASS} {
        order: -1 !important;
      }
    `;
    document.head.appendChild(style);
  })();

  /* ———————————————— API Helpers ———————————————— */

  async function getCart(forceRefresh) {
    if (!forceRefresh && cachedCartItems) {
      return { items: cachedCartItems };
    }
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      cachedCartItems = cart.items || [];
      cachedCartItemCount = cart.item_count;
      // Populate cache from cart data
      cachedCartItems.forEach(function (item) {
        if (item.handle && item.product_type) {
          productCache.set(item.handle, { type: item.product_type, handle: item.handle });
        }
      });
      return cart;
    } catch (error) {
      console.error('Cart load error:', error);
      return null;
    }
  }

  async function getProduct(handle) {
    // Check cache first
    if (productCache.has(handle)) {
      return productCache.get(handle);
    }
    try {
      const response = await fetch('/products/' + handle + '.js');
      if (!response.ok) return null;
      const product = await response.json();
      if (product) {
        productCache.set(handle, { type: product.type, handle: handle });
        return { type: product.type, handle: handle };
      }
      return null;
    } catch (error) {
      console.error('Product load error for', handle, error);
      return null;
    }
  }

  function extractHandle(href) {
    if (!href) return null;
    var match = href.match(/\/products\/([^?#/]+)/);
    return match ? match[1] : null;
  }

  /* ———————————————— Badge Text Cleaner ———————————————— */

  function watchBadgeText(badgeEl) {
    var textObserver = new MutationObserver(function () {
      var span = badgeEl.querySelector('.jdgm-prev-badge__text');
      if (span && span.textContent.includes('review')) {
        span.textContent = span.textContent.replace(/\s*reviews?\s*/i, '').trim();
      }
    });
    textObserver.observe(badgeEl, { childList: true, subtree: true, characterData: true });
  }

  /* ———————————————— DOM Injection ———————————————— */

  function insertTypeAndRating(afterEl, productType, handle, fontSize) {
    var badges = [];

    if (productType) {
      afterEl.insertAdjacentHTML('afterend',
        '<p class="' + PRODUCT_TYPE_CLASS + '" style="font-size:' + fontSize + ';font-style:italic;font-weight:400;margin:0;">' +
        productType +
        '</p>'
      );
    }

    var typeEl = afterEl.nextElementSibling;
    if (typeEl && typeEl.classList.contains(PRODUCT_TYPE_CLASS)) {
      typeEl.insertAdjacentHTML('afterend',
        '<div class="' + RATING_CLASS + '" style="margin:2px 0;">' +
        '<div class="jdgm-prev-badge" data-handle="' + handle + '"></div>' +
        '</div>'
      );

      var ratingEl = typeEl.nextElementSibling;
      var badgeEl = ratingEl ? ratingEl.querySelector('.jdgm-prev-badge') : null;
      if (badgeEl) {
        badges.push({ productHandle: handle, badgePlaceholder: badgeEl });
        watchBadgeText(badgeEl);
      }
    }

    return badges;
  }

  /* ———————————————— Upsell item injection ———————————————— */

  function insertUpsellMeta(contentEl, productType, handle) {
    var badges = [];
    var priceContainer = contentEl.querySelector('.kaching-product-upsell-item__price-container');
    if (!priceContainer) return badges;

    // Create a wrapper for our meta
    var metaDiv = document.createElement('div');
    metaDiv.className = 'kb-meta kb-meta--upsell';

    if (productType) {
      var typeSpan = document.createElement('span');
      typeSpan.className = 'kb-meta__type ' + PRODUCT_TYPE_CLASS;
      typeSpan.innerHTML = '<strong class="inspired-by-label">Inspired by</strong> ' +
        productType.replace(/^Inspired by\s*/i, '');
      metaDiv.appendChild(typeSpan);
    }

    var ratingDiv = document.createElement('div');
    ratingDiv.className = 'kb-meta__rating ' + RATING_CLASS;
    ratingDiv.innerHTML = '<div class="jdgm-prev-badge" data-handle="' + handle + '"></div>';
    metaDiv.appendChild(ratingDiv);

    // Insert after price container
    priceContainer.insertAdjacentElement('afterend', metaDiv);

    var badgeEl = ratingDiv.querySelector('.jdgm-prev-badge');
    if (badgeEl) {
      badges.push({ productHandle: handle, badgePlaceholder: badgeEl });
      watchBadgeText(badgeEl);
    }

    return badges;
  }

  /* ———————————————— Restore from cache (fast, no API) ———————————————— */

  function restoreFromCache() {
    var allBadges = [];
    var needsFullUpdate = false;

    // === Single products (not bundles) ===
    var mainTitles = document.querySelectorAll('.kaching-cart-item__title');
    mainTitles.forEach(function (title) {
      var container = title.closest('.kaching-cart-item__info');
      if (!container) return;
      if (container.querySelector('.' + PRODUCT_TYPE_CLASS)) return;
      if (container.querySelector('.kaching-cart-item-component__variants--bundle')) return;

      // Try to get handle from the title link
      var href = title.getAttribute('href');
      var handle = extractHandle(href);

      if (handle && productCache.has(handle)) {
        var data = productCache.get(handle);
        if (data.type) {
          allBadges.push.apply(allBadges, insertTypeAndRating(title, data.type, data.handle, '14px'));
        }
      } else {
        // Need full update to resolve by cart index
        needsFullUpdate = true;
      }
    });

    // === Bundle component products ===
    var bundleTitles = document.querySelectorAll('.kaching-cart-item-component__title');
    bundleTitles.forEach(function (title) {
      var content = title.closest('.kaching-cart-item-component__content');
      if (!content || content.querySelector('.' + PRODUCT_TYPE_CLASS)) return;

      var href = title.getAttribute('href');
      var handle = extractHandle(href);

      if (handle && productCache.has(handle)) {
        var data = productCache.get(handle);
        if (data.type) {
          var contentTop = title.closest('.kaching-cart-item-component__content-top');
          if (contentTop) {
            allBadges.push.apply(allBadges, insertTypeAndRating(contentTop, data.type, data.handle, '12px'));
          }
        }
      } else if (handle) {
        needsFullUpdate = true;
      }
    });

    // === Upsell carousel items ===
    var upsellItems = document.querySelectorAll('.kaching-product-upsell-item');
    upsellItems.forEach(function (item) {
      var contentEl = item.querySelector('.kaching-product-upsell-item__content');
      if (!contentEl || contentEl.querySelector('.' + PRODUCT_TYPE_CLASS)) return;

      var handle = item.getAttribute('data-product-handle');
      if (!handle) return;

      if (productCache.has(handle)) {
        var data = productCache.get(handle);
        if (data.type) {
          allBadges.push.apply(allBadges, insertUpsellMeta(contentEl, data.type, data.handle));
        }
      } else {
        needsFullUpdate = true;
      }
    });

    // Render badges if any were inserted
    if (allBadges.length) {
      setTimeout(function () {
        if (window.jdgm && window.jdgm.batchRenderBadges) {
          window.jdgm.batchRenderBadges(allBadges);
        }
      }, 300);
    }

    return needsFullUpdate;
  }

  /* ———————————————— Full Update (with API call) ———————————————— */

  async function doFullUpdate() {
    if (isUpdating) return;
    isUpdating = true;

    try {
      var mainTitles = document.querySelectorAll('.kaching-cart-item__title');
      var bundleTitles = document.querySelectorAll('.kaching-cart-item-component__title');
      var upsellItems = document.querySelectorAll('.kaching-product-upsell-item');

      if (!mainTitles.length && !bundleTitles.length && !upsellItems.length) return;

      // Single products that still need injection
      var mainToProcess = [];
      mainTitles.forEach(function (title) {
        var container = title.closest('.kaching-cart-item__info');
        if (!container) return;
        if (container.querySelector('.' + PRODUCT_TYPE_CLASS)) return;
        if (container.querySelector('.kaching-cart-item-component__variants--bundle')) return;
        mainToProcess.push(title);
      });

      // Bundle products that still need injection
      var bundleToProcess = [];
      bundleTitles.forEach(function (title) {
        var content = title.closest('.kaching-cart-item-component__content');
        if (content && !content.querySelector('.' + PRODUCT_TYPE_CLASS)) {
          bundleToProcess.push(title);
        }
      });

      // Upsell items that need injection
      var upsellToProcess = [];
      upsellItems.forEach(function (item) {
        var contentEl = item.querySelector('.kaching-product-upsell-item__content');
        if (contentEl && !contentEl.querySelector('.' + PRODUCT_TYPE_CLASS)) {
          upsellToProcess.push(item);
        }
      });

      if (!mainToProcess.length && !bundleToProcess.length && !upsellToProcess.length) return;

      // Fetch cart data (force refresh to get accurate data)
      var cart = await getCart(true);
      if (!cart || !cart.items) return;

      // Fetch missing bundle product data
      var bundleProductData = {};
      if (bundleToProcess.length) {
        var handlesToFetch = [];

        bundleToProcess.forEach(function (title) {
          var href = title.getAttribute('href');
          var handle = extractHandle(href);
          if (!handle) return;

          if (productCache.has(handle)) {
            bundleProductData[handle] = productCache.get(handle);
          } else {
            var cartItem = cart.items.find(function (item) { return item.handle === handle; });
            if (cartItem) {
              var data = { type: cartItem.product_type, handle: handle };
              productCache.set(handle, data);
              bundleProductData[handle] = data;
            } else {
              handlesToFetch.push(handle);
            }
          }
        });

        var uniqueHandles = handlesToFetch.filter(function (h, i, arr) { return arr.indexOf(h) === i; });
        if (uniqueHandles.length) {
          await Promise.all(uniqueHandles.map(async function (h) {
            var product = await getProduct(h);
            if (product) {
              bundleProductData[h] = product;
            }
          }));
        }
      }

      observer.disconnect();

      var allBadges = [];

      // Process single products
      if (mainToProcess.length) {
        var allMain = Array.from(document.querySelectorAll('.kaching-cart-item__title'));
        mainToProcess.forEach(function (title) {
          var container = title.closest('.kaching-cart-item__info');
          if (container.querySelector('.' + PRODUCT_TYPE_CLASS)) return;
          if (container.querySelector('.kaching-cart-item-component__variants--bundle')) return;

          var index = allMain.indexOf(title);
          var item = cart.items[index];
          if (!item) return;

          // Cache the data
          if (item.handle && item.product_type) {
            productCache.set(item.handle, { type: item.product_type, handle: item.handle });
          }

          allBadges.push.apply(allBadges, insertTypeAndRating(title, item.product_type, item.handle, '14px'));
        });
      }

      // Process bundle products
      if (bundleToProcess.length) {
        bundleToProcess.forEach(function (title) {
          var content = title.closest('.kaching-cart-item-component__content');
          if (content.querySelector('.' + PRODUCT_TYPE_CLASS)) return;

          var href = title.getAttribute('href');
          var handle = extractHandle(href);
          if (!handle) return;

          var data = bundleProductData[handle] || productCache.get(handle);
          if (!data || !data.type) return;

          var contentTop = title.closest('.kaching-cart-item-component__content-top');
          if (!contentTop) return;

          allBadges.push.apply(allBadges, insertTypeAndRating(contentTop, data.type, handle, '12px'));
        });
      }

      // Process upsell carousel items
      if (upsellToProcess.length) {
        // Fetch product data for upsell items not in cache
        var upsellHandlesToFetch = [];
        upsellToProcess.forEach(function (item) {
          var handle = item.getAttribute('data-product-handle');
          if (handle && !productCache.has(handle)) {
            upsellHandlesToFetch.push(handle);
          }
        });
        var uniqueUpsellHandles = upsellHandlesToFetch.filter(function (h, i, arr) { return arr.indexOf(h) === i; });
        if (uniqueUpsellHandles.length) {
          await Promise.all(uniqueUpsellHandles.map(function (h) { return getProduct(h); }));
        }

        upsellToProcess.forEach(function (item) {
          var contentEl = item.querySelector('.kaching-product-upsell-item__content');
          if (!contentEl || contentEl.querySelector('.' + PRODUCT_TYPE_CLASS)) return;

          var handle = item.getAttribute('data-product-handle');
          if (!handle) return;

          var data = productCache.get(handle);
          if (!data || !data.type) return;

          allBadges.push.apply(allBadges, insertUpsellMeta(contentEl, data.type, data.handle));
        });
      }

      observer.observe(document.body, { childList: true, subtree: true });

      if (allBadges.length) {
        setTimeout(function () {
          if (window.jdgm && window.jdgm.batchRenderBadges) {
            window.jdgm.batchRenderBadges(allBadges);
          }
        }, 300);
      }
    } finally {
      isUpdating = false;
    }
  }

  /* ———————————————— Smart Update (tries cache first) ———————————————— */

  function doUpdate() {
    // First, try fast restore from cache (no API calls)
    observer.disconnect();
    var needsFullUpdate = restoreFromCache();
    observer.observe(document.body, { childList: true, subtree: true });

    // Only do full API update if cache didn't cover everything
    if (needsFullUpdate) {
      doFullUpdate();
    }
  }

  /* ———————————————— Mutation Observer ———————————————— */

  function isIrrelevantMutation(mutation) {
    var t = mutation.target;

    // Skip mutations inside our own injected elements
    if (t.closest && t.closest('.' + PRODUCT_TYPE_CLASS)) return true;
    if (t.closest && t.closest('.' + RATING_CLASS)) return true;
    if (t.closest && t.closest('.jdgm-prev-badge')) return true;
    if (t.closest && t.closest('.jdgm-widget')) return true;
    if (t.closest && t.closest('.jdgm-preview-badge')) return true;

    // Skip mutations inside quantity controls (button clicks, input changes)
    if (t.closest && t.closest('.kaching-cart-item__quantity-controls')) return true;
    if (t.closest && t.closest('.kaching-cart-item__quantity-btn')) return true;

    // Skip price updates
    if (t.closest && t.closest('.kaching-cart-item__price-info')) return true;
    if (t.closest && t.closest('.kaching-cart-item-component__price-info')) return true;
    if (t.closest && t.closest('.kaching-cart-item__total')) return true;
    if (t.closest && t.closest('.kaching-cart-item-component__total')) return true;

    // Skip save badge updates
    if (t.closest && t.closest('.kaching-cart__badge')) return true;

    // Skip shipping bar updates
    if (t.closest && t.closest('.kaching-cart__free-shipping-bar')) return true;

    // Skip upsell item price/button updates
    if (t.closest && t.closest('.kaching-product-upsell-item__price-container')) return true;
    if (t.closest && t.closest('.kaching-product-upsell-item__add-to-cart')) return true;
    if (t.closest && t.closest('.kaching-product-upsell-item__footer')) return true;
    if (t.closest && t.closest('.kb-meta--upsell')) return true;

    return false;
  }

  function hasMissingElements() {
    // Check if any visible cart items are missing our injected elements
    var mainTitles = document.querySelectorAll('.kaching-cart-item__title');
    for (var i = 0; i < mainTitles.length; i++) {
      var container = mainTitles[i].closest('.kaching-cart-item__info');
      if (!container) continue;
      if (container.querySelector('.kaching-cart-item-component__variants--bundle')) continue;
      if (!container.querySelector('.' + PRODUCT_TYPE_CLASS)) return true;
    }

    var bundleTitles = document.querySelectorAll('.kaching-cart-item-component__title');
    for (var j = 0; j < bundleTitles.length; j++) {
      var content = bundleTitles[j].closest('.kaching-cart-item-component__content');
      if (content && !content.querySelector('.' + PRODUCT_TYPE_CLASS)) return true;
    }

    // Check upsell carousel items
    var upsellItems = document.querySelectorAll('.kaching-product-upsell-item');
    for (var k = 0; k < upsellItems.length; k++) {
      var upsellContent = upsellItems[k].querySelector('.kaching-product-upsell-item__content');
      if (upsellContent && !upsellContent.querySelector('.' + PRODUCT_TYPE_CLASS)) return true;
    }

    return false;
  }

  function onMutation(mutations) {
    // Quick check: are all mutations irrelevant?
    var hasRelevant = false;
    for (var i = 0; i < mutations.length; i++) {
      if (!isIrrelevantMutation(mutations[i])) {
        hasRelevant = true;
        break;
      }
    }
    if (!hasRelevant) return;

    // Extra check: only proceed if some elements are actually missing
    // This avoids unnecessary work when Kaching updates other parts
    if (!hasMissingElements()) return;

    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(doUpdate, 150); // Reduced from 300ms for faster recovery
  }

  var observer = new MutationObserver(onMutation);
  observer.observe(document.body, { childList: true, subtree: true });

  // Initial load — pre-warm cache
  getCart(true);
})();
